import { useState } from 'react';
import { TranslationKey } from '@/lib/core/i18n/types';
import {
  BookingDetailAPIPropertiesModel,
  ReservationDetailDto,
  ImageWithCrops,
  ServicesPresentationPropertiesModel,
  RichTextModel,
} from '@/services/platform/api/model';
import {
  useLocaleStore,
  formatDate,
  formatTimeFromDate,
  timePartIconFromDate,
  ImageMap,
  ImagesToImageMap,
  useClientTranslation,
  useUIStore,
} from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import HTMLParser from 'html-react-parser';
import { BookingDetailSummary } from '@/components/shared/booking/common/detail/summary';
import { BookingDetailTools } from '@/components/shared/booking/common/detail/tools';
import { cancelReservation, getReservation } from '@/services/platform/data/booking';
import { eventTitle } from '@/services/platform/data/booking/bookingTitle';
import { BookingCancelSheet } from '../actions/cancel-sheet';
import { BookingDetailBanner } from '@/components/shared/booking/common/detail/banner';

export interface BookingDetailProps {
  reservation: ReservationDetailDto;
  properties: BookingDetailAPIPropertiesModel;
  onCancelled?: () => void;
}

export function BookingDetail({ reservation, properties, onCancelled }: BookingDetailProps) {
  const locale = useLocaleStore(state => state.locale);
  const translation = useClientTranslation();
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const { appSettings } = useApp();
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [localReservation, setLocalReservation] = useState(reservation);

  function handleCancelBooking() {
    setCancelSheetOpen(true);
  }

  async function handleConfirmCancel() {
    const id = localReservation.publicGuid;
    if (!id) throw new Error('Missing reservation ID');
    const success = await cancelReservation(id);
    if (!success) throw new Error('Cancellation failed');
  }

  async function handleCancelSuccess() {
    const id = localReservation.publicGuid;
    if (id) {
      const updated = await getReservation(id, locale);
      if (updated) setLocalReservation(updated);
    }
    onCancelled?.();
  }

  const status = localReservation?.status ?? '';
  const serviceType = localReservation?.serviceType ?? 'Other';
  const serviceCategory = localReservation?.serviceCategory;

  const presentations = properties?.servicesPresentation?.items?.map(
    i => i?.content?.properties as ServicesPresentationPropertiesModel
  );

  const venuePresentation = presentations?.filter(p => p.serviceType == localReservation?.serviceType)?.[0];

  const venueImage =
    serviceType === 'Event'
      ? localReservation.event?.imageUrl
        ? { src: localReservation.event.imageUrl, alt: localReservation.event.title ?? '' }
        : ImagesToImageMap(appSettings?.properties?.eventNoPicImage, 'bookingDetailHero')
      : serviceType == 'Driver'
        ? ImageMap(serviceCategory?.image as ImageWithCrops, 'bookingDetailHero')
        : ImageMap(localReservation.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(localReservation.venue?.logo as ImageWithCrops);
  const venueName = localReservation.venue?.name ?? localReservation.name;
  // For an event, always the event title — its venue is already on the summary's `WHERE` row, and
  // without this the page never names the event at all. For anything else, the venue name, but only
  // as a fallback when there's no logo to show: `logo` is an optional Umbraco field (most venues
  // don't set it), and an empty hero gave no indication of which venue the booking was even for.
  const heroTitle =
    eventTitle(localReservation) ?? (serviceType !== 'Driver' && !venueLogo ? venueName : null);
  const category = serviceCategory?.name;

  const startDate = formatDate(localReservation.startDate, locale);
  const startTime = formatTimeFromDate(localReservation.startDate);
  const startTimeIcon = timePartIconFromDate(localReservation.startDate);

  const endDate = formatDate(localReservation.endDate, locale);
  //const endTime = formatTimeFromDate(localReservation.endDate);
  //const endTimeIcon = timePartIconFromDate(localReservation.endDate);

  const rangeDate =
    startDate && endDate && startDate !== endDate ? `${startDate} - ${endDate}` : startDate ? startDate : '';

  const guests = localReservation.guestCount;

  const reservations = localReservation?.reservations;
  const addons = localReservation?.reservations?.[0]?.products;

  if (serviceType == 'Driver') {
    setScreenHeadOverride({
      title: translation('App.Labels.YourRideDetails'),
      variant: 'overlay',
    });
  }

  return (
    <div className="flex flex-1 flex-col pb-24">
      {/* Banner */}
      <BookingDetailBanner
        image={venueImage}
        title={heroTitle}
        logo={heroTitle ? undefined : venueLogo}
        rows={[
          serviceType?.toLowerCase() === 'sunbed'
            ? [
                ...(rangeDate ? [{ text: rangeDate.toUpperCase() }] : []),
                ...(reservations
                  ? [
                      {
                        icon: 'sea-and-sun',
                        text: `${reservations?.length} ${reservations?.length === 1 ? translation('App.Labels.Sunbed') : translation('App.Labels.Sunbeds')}`,
                      },
                    ]
                  : []),
              ]
            : serviceType?.toLowerCase() === 'stay'
              ? [
                  ...(rangeDate ? [{ text: rangeDate.toUpperCase() }] : []),
                  ...(reservations
                    ? [
                        {
                          icon: 'shop-window',
                          text: `${reservations?.length} ${reservations?.length === 1 ? translation('App.Labels.Room') : translation('App.Labels.StayRoomsLabel')}`,
                        },
                      ]
                    : []),
                ]
              : status?.toLowerCase() != 'checkedout'
                ? [
                    ...(startDate ? [{ text: startDate.toUpperCase() }] : []),
                    ...(startTime ? [{ icon: startTimeIcon, text: startTime }] : []),
                  ]
                : [],
          [
            ...(serviceType?.toLowerCase() == 'sunbed' || serviceType?.toLowerCase() == 'stay'
              ? [
                  {
                    icon: 'hexagon-plus',
                    text: `${addons?.length ?? 0} ${(addons?.length ?? 0) === 1 ? translation('App.Labels.Addon') : translation('App.Labels.Addons')}`,
                  },
                ]
              : category && status?.toLowerCase() != 'checkedout'
                ? [{ icon: 'LayoutRight', text: category.toUpperCase() }]
                : []),
            ...(guests != null
              ? [
                  serviceType?.toLowerCase() == 'parking'
                    ? {
                        icon: 'Parking',
                        text: `${guests} ${guests === 1 ? translation('App.Labels.ParkingSpace') : translation('App.Labels.ParkingSpaces')}`,
                      }
                    : {
                        icon: 'Group',
                        text: `${guests} ${guests === 1 ? translation('App.Labels.Guest') : translation('App.Labels.Guests')}`,
                      },
                ]
              : []),
          ],
        ]}
        adjustmentsLabel={
          serviceType == 'Driver'
            ? translation('App.Labels.RideAdjustmentsText')
            : properties.adjustmentsLabel
        }
        className="booking-banner pt-28"
        style={{ marginTop: 'calc((52px + max(1rem, env(safe-area-inset-top))) * -1)' }}
      />

      {/* Main content */}
      <div className="flex flex-col gap-8 px-4 pt-12">
        {/* Ride ended text */}
        {serviceType == 'Driver' && status?.toLowerCase() == 'checkedout' && (
          <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex flex-col gap-1 text-[14px] tracking-[0.5px]">
            <div>{HTMLParser(translation('App.Labels.RideEndedMessage'))}</div>
          </div>
        )}

        {/* Confirmation text */}
        {venueName && venuePresentation && (
          <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex flex-col gap-1 text-[14px] tracking-[0.5px]">
            {venuePresentation?.yourReservation && (
              <div>
                {HTMLParser(
                  (venuePresentation?.yourReservation as RichTextModel)?.markup
                    ?.replace('{{venue.name}}', venueName)
                    ?.replace('{{status}}', translateStatus(status, translation)?.toLowerCase()) ?? ''
                )}
              </div>
            )}
            {venuePresentation?.yourReservationSchedule && status?.toLowerCase() == 'confirmed' && (
              <div>
                {HTMLParser(
                  (venuePresentation?.yourReservationSchedule as RichTextModel)?.markup
                    ?.replace('{{hour}}', startTime)
                    ?.replace('{{date}}', startDate) ?? ''
                )}
              </div>
            )}
          </div>
        )}

        {/* Booking summary */}
        <BookingDetailSummary
          reservation={localReservation}
          bookingSummaryLabel={properties.bookingSummary}
        />

        {/* Action buttons */}
        <BookingDetailTools
          reservation={localReservation}
          onCancelBooking={handleCancelBooking}
          status={status}
        />
      </div>
      <BookingCancelSheet
        open={cancelSheetOpen}
        onOpenChange={setCancelSheetOpen}
        onConfirmCancel={handleConfirmCancel}
        onSuccess={handleCancelSuccess}
      />
    </div>
  );
}

function translateStatus(status: string, t: ReturnType<typeof useClientTranslation>): string {
  const translationKey = `App.Labels.${status.charAt(0).toUpperCase() + status.slice(1)}` as TranslationKey;
  return t(translationKey);
}
